import { useNavigate } from 'react-router-dom';

import { MetricCard } from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { offeringLabel } from '@backrs_ui/grow-ui/src/pages/CoachingDashboard/namedRefLabel';
import { SEATS_TAB, SETTINGS_TAB_PARAM } from '@backrs_ui/grow-ui/src/pages/Settings/settingsTabs';
import type { AdminRoster } from '@backrs_ui/grow-ui/src/redux/institutionAdmin';

import { Box, Button, type SxProps, type Theme } from '@mui/material';

const strings = appStrings.modules.rostersAdmin;

export type SeatTotal = Readonly<{
  /** Kebab-case, so it can carry a DOM id. */
  key: string;
  label: string;
  seatsUsed: number;
  /** Null where any roster in the group is uncapped, which leaves the group's total unknown. */
  capacity: number | null;
}>;

const slug = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

/**
 * Rosters sharing an offering add together, first appearance first. Offerings carrying no id
 * group by label, so rosters whose offering has neither id nor name merge under one card.
 */
export const seatTotals = (rosters: readonly AdminRoster[]): SeatTotal[] => {
  const byOffering = new Map<string, SeatTotal>();

  rosters.forEach((roster) => {
    const label = offeringLabel(roster.offering);
    const key = roster.offering.id === null ? slug(label) : String(roster.offering.id);
    const running = byOffering.get(key);

    byOffering.set(
      key,
      running === undefined
        ? { key, label, seatsUsed: roster.seatsUsed, capacity: roster.capacity }
        : {
            ...running,
            seatsUsed: running.seatsUsed + roster.seatsUsed,
            capacity:
              running.capacity === null || roster.capacity === null
                ? null
                : running.capacity + roster.capacity,
          },
    );
  });

  return Array.from(byOffering.values());
};

export const SeatSummary = ({ rosters }: Readonly<{ rosters: readonly AdminRoster[] }>) => {
  const navigate = useNavigate();
  const totals = seatTotals(rosters);

  if (totals.length === 0) return null;

  return (
    <Box component="section" aria-label={strings.seatSummary.regionAria} sx={rowSx}>
      {totals.map((total) => (
        <Box key={total.key} sx={cardWidthSx}>
          <MetricCard
            id={`rosters-admin-seat-total-${total.key}`}
            label={total.label}
            value={strings.capacity.usage(total.seatsUsed, total.capacity)}
          />
        </Box>
      ))}
      <Button
        id="rosters-admin-seat-summary-contract"
        hierarchy="Secondary gray"
        size="md"
        sx={contractButtonSx}
        onClick={() => navigate(`${APP_ROUTE.SETTINGS}?${SETTINGS_TAB_PARAM}=${SEATS_TAB}`)}
      >
        {strings.seatSummary.contractLink}
      </Button>
    </Box>
  );
};

const rowSx: SxProps<Theme> = ({ backrs }) => ({
  display: { xs: 'none', md: 'flex' },
  flexWrap: 'nowrap',
  alignItems: 'flex-end',
  justifyContent: 'flex-end',
  flexShrink: 0,
  gap: backrs.spacing['spacing-xl'],
});

const contractButtonSx: SxProps<Theme> = {
  flexShrink: 0,
  whiteSpace: 'nowrap',
};

const cardWidthSx: SxProps<Theme> = {
  flex: '0 0 auto',
  minWidth: 160,
};
